import { MPOST } from 'common/helpers.ts';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useSWRMutation from 'swr/mutation';
import { Button } from 'ui/component/Button.tsx';
import { ICheck } from 'ui/component/Icons.tsx';
import { Modal } from 'ui/component/Modal.tsx';
import { Spinner } from 'ui/component/Spinner.tsx';
import { Input } from 'ui/control/Input.tsx';
import { getTranslatedError } from '#admin/helpers.ts';
import { useMe } from '#admin/hook/useMe.tsx';

type FormValues = {
  label: string;
};

export type ApiKeyFormProps = {
  onSave?: () => void;
  onClose?: () => void;
  isOpen: boolean;
};

export const ApiKeyForm = ({ onSave, onClose, isOpen }: ApiKeyFormProps) => {
  const { t } = useTranslation();
  const { me } = useMe();
  const createMutation = useSWRMutation('/api-key', MPOST);
  const [generatedKey, setGeneratedKey] = React.useState<string | null>(null);

  const isLoading = !!createMutation.isMutating;
  const isError = createMutation.error;

  const formMethods = useForm<FormValues>({
    mode: 'onTouched',
    criteriaMode: 'all',
    reValidateMode: 'onChange',
    shouldUnregister: true,
    defaultValues: {
      label: '',
    },
  });

  const { handleSubmit, formState } = formMethods;

  const onSubmit = async ({ label }: FormValues) => {
    if (generatedKey) {
      await navigator?.clipboard?.writeText(generatedKey);
      setGeneratedKey(null);
      if (onSave) onSave();
      return;
    }
    const result = await createMutation.trigger({ label });
    setGeneratedKey(result?.key);
  };

  const handleClose = () => {
    setGeneratedKey(null);
    if (onClose) onClose();
  };

  return (
    <Modal title={<h3>{t('apiKey.form.title')}</h3>} isOpen={isOpen} onClose={handleClose}>
      {(!me || isLoading) && <Spinner centered={true} overlay={true} blur={true} />}

      <FormProvider {...formMethods}>
        <form className="flex w-[600px] flex-col gap-6" autoCorrect={'off'} autoComplete={'off'} onSubmit={handleSubmit(onSubmit)} noValidate={true}>
          <Input
            className="w-full"
            name={'label'}
            label={t('apiKey.form.label')}
            placeholder={t('apiKey.form.labelPlaceholder')}
            registerOptions={{ required: true }}
            disabled={generatedKey !== null}
            autoFocus={false}
          >
            {formState.errors.label?.type === 'required' ? t('apiKey.form.labelRequired') : null}
          </Input>

          {generatedKey && (
            <div className="flex flex-col gap-2">
              <p>{t('apiKey.form.copyMessage')}</p>
              <p>
                <b>{t('apiKey.form.warningMessage')}</b>
              </p>
              <div className="flow-text break-words border border-dashed bg-slate-50 p-2 font-mono">{generatedKey}</div>
            </div>
          )}

          <div className="flex items-center gap-4">
            <Button type="button" className="blue-outlined col-span-2 justify-self-end" onClick={onClose}>
              {t('apiKey.form.buttons.back')}
            </Button>
            <Button
              type={'submit'}
              className="blue col-span-2 justify-self-end"
              loading={isLoading}
              disabled={!formState.isValid || !formState.isDirty}
              RightIcon={!isLoading && formState.isSubmitSuccessful ? ICheck : undefined}
            >
              {generatedKey ? t('apiKey.form.buttons.copy') : t('apiKey.form.buttons.generate')}
            </Button>
          </div>

          {!!isError && <pre className="red">{getTranslatedError(isError, t) || t('apiKey.form.unknownError')}</pre>}
        </form>
      </FormProvider>
    </Modal>
  );
};
